import React from 'react';
import { useRouting } from './useRouting';
import { useLocation } from 'react-router-dom';

type QueryState =
  | number
  | string
  | boolean
  | null
  | undefined
  | Record<string, unknown>;

const STRING_URL_ENCODED = Symbol('STRING_URL_ENCODED');
export type URLEncodedString = {
  tag: typeof STRING_URL_ENCODED;
  value: string;
};
export const urlEncode = (value: string): URLEncodedString => {
  return {
    tag: STRING_URL_ENCODED,
    value: encodeURIComponent(value),
  };
};

export type UseQueryStateParameters<State> = {
  encode: (state: State) => URLEncodedString;
  decode: (
    value: string
  ) => { tag: 'Success'; value: State } | { tag: 'Failed' };
};

export const useQueryState = <State extends QueryState, Key extends string>(
  encode: UseQueryStateParameters<State>['encode'],
  decode: UseQueryStateParameters<State>['decode'],
  fallbackState: State,
  queryKey: Key
): [State, React.Dispatch<React.SetStateAction<State>>] => {
  const { replaceRouting } = useRouting();
  const { search, hash, pathname, state: locationState } = useLocation();
  const queryObject = new URLSearchParams(search);
  const state_: ReturnType<typeof decode> = (value => {
    if (value === null) return { tag: 'Failed' } as const;
    return decode(decodeURIComponent(value));
  })(queryObject.get(queryKey));
  const state = state_.tag === 'Success' ? state_.value : fallbackState;
  const setState: React.Dispatch<React.SetStateAction<State>> = (
    val: React.SetStateAction<State>
  ) => {
    const nextState = encode(typeof val === 'function' ? val(state) : val)
      .value;
    queryObject.set(queryKey, nextState);
    queryObject.sort();
    const query = queryObject.toString();
    const path = `${pathname}?${query}${hash}`;
    if (`?${query}` !== search) {
      replaceRouting(
        {
          path,
          state: locationState,
        },
        true
      );
    }
  };
  return [state, setState];
};
